import React, { useEffect, useState, useRef } from 'react';
import { BellIcon, MenuIcon, UserIcon, SettingsIcon, LogOutIcon, Gem, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import NotificationsMenu from './NotificationsMenu';
interface TopbarProps {
  onMenuClick?: () => void;
  onOpenAIChat?: () => void;
}
const Topbar: React.FC<TopbarProps> = ({
  onMenuClick,
  onOpenAIChat
}) => {
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const handleLogout = () => {
    // In a real app, you would clear authentication tokens/cookies here
    navigate('/login');
  };
  // Close notifications and profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return <div className="h-16 md:h-20 bg-white sticky top-0 z-30 border-b border-gray-100 flex items-center justify-between px-4 md:px-6" data-id="element-1966">
      <div className="flex items-center gap-4" data-id="element-1967">
        {/* Mobile menu button */}
        <button type="button" className="md:hidden -ml-1 h-10 w-10 inline-flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50 focus:outline-none transition-colors" onClick={onMenuClick} data-id="element-1968">
          <span className="sr-only" data-id="element-1969">Open sidebar</span>
          <MenuIcon className="h-6 w-6" aria-hidden="true" strokeWidth={1.5} data-id="element-1970" />
        </button>

        {/* Mobile Logo */}
        <div className="md:hidden flex items-center" data-id="element-1971">
          <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback Logo" className="h-6 object-contain" data-id="element-1972" />
        </div>

        {/* AI Chat Button - Gradient Outline Style */}
        <div className="p-[2px]" style={{
        background: 'linear-gradient(90deg, rgba(255, 0, 77, 1) 0%, rgba(116, 10, 214, 1) 100%)',
        borderRadius: '100px'
      }} data-id="element-1973">
          <button onClick={onOpenAIChat} className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 transition-colors font-medium text-sm text-gray-900" style={{
          borderRadius: '100px'
        }} data-id="element-1974">
            <span className="hidden sm:inline" data-id="element-1975">GB Intelligence</span>
            <span className="sm:hidden" data-id="element-1976">Intelligence</span>
          </button>
        </div>
      </div>

      <div className="flex items-center space-x-3" data-id="element-1977">
        {/* Diamond and Star Indicators */}
        <div className="flex items-center gap-3" data-id="element-1978">
          <button onClick={() => setShowCreditsModal(true)} className="flex items-center gap-1.5 transition-colors duration-300 cursor-pointer hover:opacity-80 text-green-600" data-id="element-1979">
            <Gem className="h-5 w-5" strokeWidth={2} data-id="element-1980" />
            <span className="text-sm font-normal" data-id="element-1981">150</span>
          </button>
          <button onClick={() => setShowCreditsModal(true)} className="flex items-center gap-1.5 transition-colors duration-300 cursor-pointer hover:opacity-80 text-gray-600" data-id="element-1982">
            <Star className="h-5 w-5" strokeWidth={2} data-id="element-1983" />
            <span className="text-sm font-normal" data-id="element-1984">400</span>
          </button>
        </div>

        {/* Notifications */}
        <div className="relative" ref={notificationsRef} data-id="element-1985">
          <button onClick={() => setIsNotificationsOpen(!isNotificationsOpen)} className="relative hover:bg-gray-50 p-2 rounded-full transition-colors" data-id="element-1986">
            <BellIcon className="text-gray-600 cursor-pointer hover:text-gray-900" size={20} strokeWidth={1.5} data-id="element-1987" />
            <span className="absolute -top-1 -right-1 bg-green-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center font-normal" data-id="element-1988">
              3
            </span>
          </button>
          <NotificationsMenu isOpen={isNotificationsOpen} onClose={() => setIsNotificationsOpen(false)} data-id="element-1989" />
        </div>

        {/* Profile Menu */}
        <div className="relative" ref={profileMenuRef} data-id="element-1990">
          <button onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)} className="flex items-center space-x-3 hover:bg-gray-50 px-2 py-1.5 rounded-full transition-colors" data-id="element-1991">
            <div className="h-9 w-9 rounded-full overflow-hidden border-2 border-green-600" data-id="element-1992">
              <img src="https://cdn.magicpatterns.com/uploads/wAuXNp8Njv5DkmF4gEUeC7/jonathan-cooper-ViyA5myhBVw-unsplash.jpg" alt="Profilo" className="h-full w-full object-cover" data-id="element-1993" />
            </div>
            <div className="hidden md:block text-left" data-id="element-1994">
              <div className="text-sm font-normal text-gray-900" data-id="element-1995">
                Alex Rossi
              </div>
              <div className="text-xs text-gray-500 font-normal" data-id="element-1996">
                Ricercatore PhD
              </div>
            </div>
          </button>

          {/* Profile Dropdown Menu */}
          {isProfileMenuOpen && <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl py-1 z-50 border border-gray-100" data-id="element-1997">
              <div className="px-4 py-3 border-b border-gray-100" data-id="element-1998">
                <p className="text-sm font-normal text-gray-900" data-id="element-1999">Alex Rossi</p>
                <p className="text-xs text-gray-500 font-normal truncate" data-id="element-2000">
                  alex.rossi@example.com
                </p>
              </div>
              <ul data-id="element-2001">
                <li data-id="element-2002">
                  <button onClick={() => {
                setIsProfileMenuOpen(false);
                navigate('/public-profile');
              }} className="flex items-center px-4 py-2 text-sm font-normal text-gray-600 hover:bg-gray-50 w-full text-left transition-colors" data-id="element-2003">
                    <UserIcon size={16} className="mr-2" strokeWidth={1.5} data-id="element-2004" />
                    Il mio profilo
                  </button>
                </li>
                <li data-id="element-2005">
                  <button onClick={() => setIsProfileMenuOpen(false)} className="flex items-center px-4 py-2 text-sm font-normal text-gray-600 hover:bg-gray-50 w-full text-left transition-colors" data-id="element-2006">
                    <SettingsIcon size={16} className="mr-2" strokeWidth={1.5} data-id="element-2007" />
                    Impostazioni
                  </button>
                </li>
                <li className="border-t border-gray-100" data-id="element-2008">
                  <button onClick={handleLogout} className="flex items-center px-4 py-2 text-sm font-normal text-red-600 hover:bg-gray-50 w-full text-left transition-colors" data-id="element-2009">
                    <LogOutIcon size={16} className="mr-2" strokeWidth={1.5} data-id="element-2010" />
                    Logout
                  </button>
                </li>
              </ul>
            </div>}
        </div>
      </div>
    </div>;
};
export default Topbar;